<template>
  <section id="how-it-works" class="how-it-works" ref="sectionRef">
    <div class="scrolly-container">
      <!-- Sticky SVG background -->
      <div class="sticky-visual">
        <svg
          class="pipeline-svg"
          viewBox="0 0 1000 400"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Data pipeline architecture diagram showing three stages: Ingestion of multi-source data, Agentic AI Processing with LLM-powered reasoning chains, and Insight Routing to dashboards and downstream applications"
        >
          <title>Data Do Agentic Data Pipeline Architecture</title>
          <desc>A technical diagram illustrating Data Do's three-stage data pipeline: Stage 1 (Ingestion) collects data from APIs, databases, documents, and real-time streams into a unified pipeline. Stage 2 (Agentic Processing) uses AI agents to autonomously classify, enrich, and transform data with LLM-powered reasoning chains and quality gates. Stage 3 (Insight Routing) delivers processed insights to dashboards, alerting systems, and downstream applications with full lineage tracking. Animated particles flow along the pipeline backbone connecting all three stages.</desc>
          <!-- Pipeline backbone -->
          <line
            x1="100" y1="200" x2="900" y2="200"
            class="pipeline-line"
            :style="{ strokeDashoffset: lineOffset }"
          />

          <!-- Stage 1: Ingestion -->
          <g class="stage-group" :class="{ active: activeStage >= 0 }">
            <circle cx="200" cy="200" r="50" class="stage-circle" />
            <text x="200" y="195" class="stage-icon">📥</text>
            <text x="200" y="280" class="stage-label">Ingestion</text>
            <!-- Data particles -->
            <circle cx="120" cy="180" r="4" class="particle p1" />
            <circle cx="130" cy="220" r="3" class="particle p2" />
            <circle cx="140" cy="195" r="5" class="particle p3" />
          </g>

          <!-- Stage 2: Agentic Processing -->
          <g class="stage-group" :class="{ active: activeStage >= 1 }">
            <circle cx="500" cy="200" r="55" class="stage-circle processing" />
            <text x="500" y="195" class="stage-icon">🤖</text>
            <text x="500" y="280" class="stage-label">Agentic Processing</text>
            <!-- Gear/processing indicators -->
            <circle cx="460" cy="155" r="8" class="orbit o1" />
            <circle cx="540" cy="155" r="6" class="orbit o2" />
            <circle cx="500" cy="140" r="5" class="orbit o3" />
          </g>

          <!-- Stage 3: Insight Routing -->
          <g class="stage-group" :class="{ active: activeStage >= 2 }">
            <circle cx="800" cy="200" r="50" class="stage-circle" />
            <text x="800" y="195" class="stage-icon">📊</text>
            <text x="800" y="280" class="stage-label">Insight Routing</text>
            <!-- Output arrows -->
            <line x1="830" y1="170" x2="880" y2="140" class="route-line r1" />
            <line x1="830" y1="200" x2="890" y2="200" class="route-line r2" />
            <line x1="830" y1="230" x2="880" y2="260" class="route-line r3" />
          </g>

          <!-- Flow particles along pipeline -->
          <circle r="6" class="flow-dot" :class="{ animating: activeStage >= 0 }">
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              path="M200,200 L500,200 L800,200"
            />
          </circle>
          <circle r="4" class="flow-dot secondary" :class="{ animating: activeStage >= 1 }">
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              begin="0.5s"
              path="M200,200 L500,200 L800,200"
            />
          </circle>
        </svg>
      </div>

      <!-- Scroll-driven text panels -->
      <div class="scroll-panels">
        <div
          v-for="(stage, i) in stages"
          :key="i"
          class="scroll-panel"
          :ref="el => { if (el) panelRefs[i] = el }"
        >
          <div class="panel-content" :class="{ visible: activeStage === i }">
            <h3 class="panel-step">{{ stage.step }}</h3>
            <h2 class="panel-title">{{ stage.title }}</h2>
            <p class="panel-desc">{{ stage.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'

const stages = [
  {
    step: 'Step 1',
    title: 'Ingestion',
    description: 'Multi-source data ingestion from APIs, databases, documents, and real-time streams — unified into a single processing pipeline.'
  },
  {
    step: 'Step 2',
    title: 'Agentic Processing',
    description: 'AI agents autonomously classify, enrich, and transform your data using LLM-powered reasoning chains with built-in quality gates.'
  },
  {
    step: 'Step 3',
    title: 'Insight Routing',
    description: 'Processed insights are routed to dashboards, alerting systems, and downstream applications in real-time with full lineage tracking.'
  }
]

const sectionRef = ref(null)
const panelRefs = reactive({})
const activeStage = ref(-1)
const scrollProgress = ref(0)

const lineOffset = computed(() => {
  const total = 800
  return total - (total * Math.min(scrollProgress.value, 1))
})

let observer = null

const handleScroll = () => {
  if (!sectionRef.value) return
  const rect = sectionRef.value.getBoundingClientRect()
  const sectionHeight = rect.height - window.innerHeight
  if (sectionHeight <= 0) return
  const rawProgress = -rect.top / sectionHeight
  scrollProgress.value = Math.max(0, Math.min(1, rawProgress))
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idx = Object.values(panelRefs).indexOf(entry.target)
          if (idx !== -1) activeStage.value = idx
        }
      })
    },
    { threshold: 0.5 }
  )

  Object.values(panelRefs).forEach((el) => {
    if (el) observer.observe(el)
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.how-it-works {
  background: #0a0e1a;
  color: white;
  position: relative;
}

.scrolly-container {
  position: relative;
  min-height: 300vh;
}

/* Sticky SVG visual */
.sticky-visual {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  pointer-events: none;
}

.pipeline-svg {
  width: 90%;
  max-width: 900px;
  height: auto;
}

/* Pipeline line with draw-on effect */
.pipeline-line {
  stroke: rgba(0, 85, 255, 0.3);
  stroke-width: 2;
  stroke-dasharray: 800;
  stroke-dashoffset: 800;
  transition: stroke-dashoffset 0.1s linear;
  will-change: stroke-dashoffset;
}

/* Stage circles */
.stage-circle {
  fill: rgba(0, 85, 255, 0.08);
  stroke: rgba(0, 85, 255, 0.2);
  stroke-width: 2;
  transition: fill 0.6s ease, stroke 0.6s ease;
  will-change: fill, stroke;
}

.stage-group.active .stage-circle {
  fill: rgba(0, 85, 255, 0.2);
  stroke: rgba(0, 85, 255, 0.8);
}

.stage-group.active .stage-circle.processing {
  fill: rgba(0, 85, 255, 0.25);
}

.stage-icon {
  font-size: 28px;
  text-anchor: middle;
  dominant-baseline: central;
  opacity: 0.3;
  transition: opacity 0.6s ease;
}

.stage-group.active .stage-icon {
  opacity: 1;
}

.stage-label {
  text-anchor: middle;
  fill: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  font-weight: 600;
  font-family: "JetBrains Mono", monospace;
  transition: fill 0.6s ease;
}

.stage-group.active .stage-label {
  fill: rgba(255, 255, 255, 0.9);
}

/* Particles */
.particle, .orbit {
  fill: rgba(0, 85, 255, 0.2);
  transition: fill 0.6s ease;
}

.stage-group.active .particle,
.stage-group.active .orbit {
  fill: rgba(0, 85, 255, 0.7);
}

.stage-group.active .orbit {
  animation: orbitPulse 2s ease-in-out infinite;
  will-change: transform, opacity;
}

.orbit.o1 { animation-delay: 0s; }
.orbit.o2 { animation-delay: 0.3s; }
.orbit.o3 { animation-delay: 0.6s; }

@keyframes orbitPulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.3); }
}

/* Route lines */
.route-line {
  stroke: rgba(0, 85, 255, 0.15);
  stroke-width: 2;
  stroke-linecap: round;
  transition: stroke 0.6s ease;
}

.stage-group.active .route-line {
  stroke: rgba(0, 85, 255, 0.7);
}

/* Flow dots */
.flow-dot {
  fill: var(--primary-color, #0055ff);
  opacity: 0;
  will-change: opacity;
}

.flow-dot.animating {
  opacity: 0.8;
}

.flow-dot.secondary {
  fill: rgba(0, 85, 255, 0.5);
}

/* Scroll panels overlaying the sticky visual */
.scroll-panels {
  position: relative;
  z-index: 2;
  pointer-events: none;
}

.scroll-panel {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.panel-content {
  background: rgba(10, 14, 26, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 85, 255, 0.15);
  border-radius: 16px;
  padding: 40px;
  max-width: 480px;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  pointer-events: auto;
}

.panel-content.visible {
  opacity: 1;
  transform: translateY(0);
}

.panel-step {
  font-family: "JetBrains Mono", monospace;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: var(--primary-color, #0055ff);
  margin-bottom: 8px;
}

.panel-title {
  font-size: 1.8rem;
  margin-bottom: 16px;
  line-height: 1.2;
}

.panel-desc {
  font-size: 1rem;
  line-height: 1.7;
  opacity: 0.8;
}

@media (max-width: 768px) {
  .panel-content {
    padding: 24px;
    max-width: 100%;
  }
  .panel-title {
    font-size: 1.4rem;
  }
  .stage-label {
    font-size: 10px;
  }
}
</style>
